import type {AssigneePickerBaseAssignee} from '@github-ui/item-picker-assignee'
import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import type {
  replaceActorsForAssignableRelayMutation,
  replaceActorsForAssignableRelayMutation$data,
} from './__generated__/replaceActorsForAssignableRelayMutation.graphql'

export type AssigneePickerAssignee = AssigneePickerBaseAssignee & {
  type?: string
}

export function commitUpdateIssueAssigneesMutation({
  environment,
  input: {issueId, assignedActors, participants},
  clientMutationId,
  onError,
  onCompleted,
}: {
  environment: Environment
  input: {issueId: string; assignedActors: AssigneePickerAssignee[]; participants: AssigneePickerAssignee[]}
  clientMutationId?: string
  onError?: (error: Error) => void
  onCompleted?: (response: replaceActorsForAssignableRelayMutation$data) => void
}) {
  // merge assigned actors and participants into one, unique by login and take the first 5
  // do not include copilot or agents because bots do not show up as participants on issues
  // (the participants field on Issue returns UserConnection, which only contains User types)
  const newParticipants = [
    ...new Map(
      [...assignedActors, ...participants].filter(a => !a.isCopilot && !a.isAgent).map(item => [item.login, item]),
    ).values(),
  ].slice(0, 5)

  // we use the `replaceActorsForAssignable` mutation specifically instead of `editIssue` as this has more specific permission
  // check for assignments.
  return commitMutation<replaceActorsForAssignableRelayMutation>(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation replaceActorsForAssignableRelayMutation($input: ReplaceActorsForAssignableInput!) @raw_response_type {
        replaceActorsForAssignable(input: $input) {
          assignable {
            ... on Issue {
              id
              assignedActors(first: 20) {
                nodes {
                  ... on Actor {
                    __typename
                    id
                    login
                    name
                    profileResourcePath
                    avatarUrl(size: 64)
                    ... on Bot {
                      isCopilot
                      isAgent
                      displayName
                    }
                  }
                }
              }
              agentAssignments {
                assignee {
                  id
                  login
                  avatarUrl(size: 64)
                  isCopilot
                  isAgent
                  displayName
                }
                assigner {
                  ... on User {
                    login
                  }
                  ... on Bot {
                    login
                  }
                }
              }
              participants(first: 10) {
                nodes {
                  id
                  login
                  name
                  profileResourcePath
                  avatarUrl(size: 64)
                }
              }
            }
          }
        }
      }
    `,
    variables: {input: {assignableId: issueId, actorIds: assignedActors.map(a => a.id), clientMutationId}},
    optimisticResponse: {
      replaceActorsForAssignable: {
        assignable: {
          __typename: 'Issue',
          __isNode: 'Issue',
          id: issueId,
          assignedActors: {
            nodes: assignedActors.map(a => {
              if (a.__typename === 'Bot') {
                return {
                  __typename: 'Bot',
                  __isActor: 'Bot',
                  __isNode: 'Bot',
                  id: a.id,
                  login: a.login,
                  name: a.name ?? null,
                  avatarUrl: a.avatarUrl,
                  profileResourcePath: a.profileResourcePath,
                  displayName: a.displayName ?? '',
                  isCopilot: a.isCopilot || false,
                  isAgent: a.isAgent || false,
                }
              } else {
                return {
                  __typename: 'User',
                  __isActor: 'User',
                  __isNode: 'User',
                  id: a.id,
                  login: a.login,
                  name: a.name ?? null,
                  avatarUrl: a.avatarUrl,
                  profileResourcePath: a.profileResourcePath,
                }
              }
            }),
          },
          agentAssignments: [],
          participants: {
            nodes: newParticipants.map(a => ({
              id: a.id,
              login: a.login,
              name: a.name ?? null,
              avatarUrl: a.avatarUrl,
              profileResourcePath: a.profileResourcePath,
            })),
          },
        },
      },
    },
    onError: error => onError && onError(error),
    onCompleted: response => onCompleted && onCompleted(response),
  })
}
